import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useRealtimeCrime
 * Subscribes to crime_attempts, player_stats (cooldown/evidence/jail), and evidence_tracker.
 */
export function useRealtimeCrime({
  userId,
  onCrimeAttempt,
  onStatsUpdate,
  onEvidenceUpdate,
  onJailUpdate,
  enabled = true,
}) {
  const [status, setStatus] = useState('idle');
  const channelsRef = useRef([]);
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    channelsRef?.current?.forEach(ch => {
      try { supabase?.removeChannel(ch); } catch (e) {}
    });
    channelsRef.current = [];
  }, []);

  const subscribe = useCallback(() => {
    if (!userId || !enabled || !mountedRef?.current) return;
    cleanup();
    setStatus('connecting');

    // 1. crime_attempts INSERT — live activity feed
    const attemptsChannel = supabase
      ?.channel(`realtime-crime-attempts-${userId}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'crime_attempts',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        onCrimeAttempt?.(payload?.new);
      })
      ?.subscribe((s) => {
        if (!mountedRef?.current) return;
        if (s === 'SUBSCRIBED') setStatus('connected');
        else if (s === 'CHANNEL_ERROR' || s === 'TIMED_OUT') setStatus('error');
      });

    // 2. player_stats UPDATE — cooldown timer, evidence, jail status
    const statsChannel = supabase
      ?.channel(`realtime-crime-stats-${userId}`)
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'player_stats',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        const newData = payload?.new;
        onStatsUpdate?.(newData);
        // Separate evidence update
        if (newData?.evidence_level !== undefined) {
          onEvidenceUpdate?.(parseFloat(newData?.evidence_level || 0));
        }
        // Jail status update
        if (newData?.jail_until !== undefined) {
          onJailUpdate?.(newData?.jail_until);
        }
      })
      ?.subscribe();

    // 3. evidence_tracker UPDATE
    const evidenceChannel = supabase
      ?.channel(`realtime-evidence-${userId}`)
      ?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'evidence_tracker',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        const ev = payload?.new?.evidence_level;
        if (ev !== undefined) onEvidenceUpdate?.(parseFloat(ev || 0));
      })
      ?.subscribe();

    channelsRef.current = [attemptsChannel, statsChannel, evidenceChannel]?.filter(Boolean);
  }, [userId, enabled, onCrimeAttempt, onStatsUpdate, onEvidenceUpdate, onJailUpdate, cleanup]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled && userId) subscribe();
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [subscribe, enabled, userId]);

  return { status };
}
